import { authAxios } from '@components/Auth';
import humps from 'lodash-humps-ts';
import { InsurancePolicyUserList, InsurancePolicyUser } from '@models/InsurancePolicyUser';
import { API_V1_BASE } from '@config';
import { snakes } from '@utils/lodash';
import { WithParams, Numeric } from '@utils/typescript';
import { paginate, Pagination, PaginationParams } from '@models/Pagination';
import { BenefitLimit } from '@models/BenefitLimit';

export type GetAllInsurancePolicyUserParams = PaginationParams & WithParams<{
  insurance_policy_id: number;
  search?: string;
}>;

export type CreateInsurancePolicyUserParams = WithParams<{
  user_id: number;
  insurance_policy_id: number;
  benefit_template_id: number;
  insurer_member_id: string;
  premium: Numeric;
  refunded_premium: Numeric;
  effective_date: string;
  expiration_date: string;
  description_html: string;
}>;

export type UpdateInsurancePolicyUserParams = Omit<CreateInsurancePolicyUserParams, 'userId' | 'insurancePolicyId'> & WithParams<{
  id: number;
}>;

export type GetInsurancePolicyUserParams = { id: number };

export type SearchInsurancePolicyUserParams = PaginationParams & WithParams<{
  search?: string;
  policyNumber?: string;
}>;

export class InsurancePolicyUserApi {
  static getAll = async (params: GetAllInsurancePolicyUserParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policy_users`, { params: snakes(params) })
      .then((response) => paginate(response) as [InsurancePolicyUserList[], Pagination]);
  };

  static get = async ({ id }: GetInsurancePolicyUserParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policy_users/${id}`)
      .then((response) => humps(response.data) as InsurancePolicyUser);
  };

  static getHealthBenefitLimitsWithAccumulatedPaidAmounts = async ({ id }: GetInsurancePolicyUserParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policy_users/${id}/health_benefit_limits_with_accumulated_paid_amounts`)
      .then((response) => humps(response.data) as BenefitLimit[]);
  };

  static create = async (params: CreateInsurancePolicyUserParams) => {
    return await authAxios
      .postForm(`${API_V1_BASE}/user/insurance_policy_users`, snakes(params));
  };

  static update = async ({ id, ...params }: UpdateInsurancePolicyUserParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/insurance_policy_users/${id}`, snakes(params));
  };

  static search = async (params: SearchInsurancePolicyUserParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policy_users/search`, { params: snakes(params) })
      .then((response) => paginate(response) as [InsurancePolicyUserList[], Pagination]);
  };
}
